// Power-ups: the Godot game's held-slot buffs (scripts/player/powerup_catalog.gd, player.gd
// _activate_powerup), on the web. A drop sits on the ground glowing in its colour; walk over it
// and it goes in your slot (a full slot leaves it lying there for someone else). Press the
// power-up button (touchpad / B) to fire it. Timed ones run on a clock and show on you; instant
// ones land at once. One timed buff at a time, as in Godot.
//
// Where they come from (sim/run.ts): one drops on every combat island clear, and the bonus
// island's prize vault pays two per cowboy and opens a second slot for the rest of the run.
//
// Balance (James gave liberty, 2026-10-21): Godot ran every timed buff for 90 s, most of an
// island. Here the defensive skins last 20 s (a fight, not a whole island) and the rest 30 s;
// Metal Skin softens direct hits to 30 % instead of bouncing everything; Magnetic Skin swats
// three in four robot bolts out of the air near you (and your teammates standing in the field);
// +1 STIM is Godot's again now the web game has a stim belt: one more bottle of whiskey (sim/whiskey.ts),
// whose 10 s heal-over-time (stimT) is shown as this buff while it runs. Numbers that were already sensible stay Godot's: Minor Heal 40 % hull, Overshield
// one full shield's worth, Super Coolant halves heat gain.

import { secs } from './core';
import { CLASSES, HULL_MAX } from './defs';
import { hullMaxOf } from './cards';
import { modsOf } from './player';
import { Ev } from './codec';
import { Phase, PlayerState } from './player';
import { Elem, GRENADE_MAX, NO_WEAPON, WSlot, weaponDef } from './weapons';
import type { Game } from './game';
import { HEAL_HPS, WHISKEY_MAX } from './whiskey';

export const enum PU {
  None = 0, MetalSkin, MagneticSkin, FullCharge, MinorHeal, Stim, ElementalPower, Overshield, SuperCoolant,
}
export const PU_COUNT = 9;

export interface PUDef {
  name: string;
  /** 0xRRGGBB, the drop's glow and the HUD chip */
  color: number;
  /** seconds it runs for; 0 = instant */
  secs: number;
  /** what it does, for the pickup notice and the HUD */
  blurb: string;
}

export const PU_DEFS: PUDef[] = [
  { name: '', color: 0xffffff, secs: 0, blurb: '' },
  { name: 'METAL SKIN', color: 0xbfc7d1, secs: 20, blurb: 'DIRECT HITS DO 30 %' },
  { name: 'MAGNETIC SKIN', color: 0xb85aff, secs: 20, blurb: 'BENDS ROBOT FIRE AWAY' },
  { name: 'FULL CHARGE', color: 0x4da6ff, secs: 0, blurb: 'GRENADES, SPECIAL AND SHIELD TOPPED UP' },
  { name: 'MINOR HEAL', color: 0x59f273, secs: 0, blurb: '+40 % HULL' },
  { name: 'WHISKEY', color: 0xf2a23a, secs: 10, blurb: 'ONE MORE BOTTLE IN YOUR BELT' },
  { name: 'POWER', color: 0xffffff, secs: 30, blurb: 'YOUR SHOTS CARRY IT; IT CANNOT HURT YOU' },
  { name: 'OVERSHIELD', color: 0xffd959, secs: 0, blurb: 'A SECOND SHIELD ON TOP' },
  { name: 'SUPER COOLANT', color: 0x73e6ff, secs: 30, blurb: 'HALF HEAT ON EVERYTHING' },
];

/** Elemental Power rolls one of these (no Magnetic: that's Magnetic Skin's identity). */
export const PU_ELEMENTS = [Elem.Fire, Elem.Ice, Elem.Shock, Elem.Acid];
export const ELEM_NAME: Record<number, string> = { [Elem.Fire]: 'FIRE', [Elem.Ice]: 'ICE', [Elem.Shock]: 'SHOCK', [Elem.Acid]: 'ACID' };
export const ELEM_COLOR: Record<number, number> = { [Elem.Fire]: 0xff7a2e, [Elem.Ice]: 0x8fdcff, [Elem.Shock]: 0xffe94d, [Elem.Acid]: 0x9bff3c };

export const METAL_TAKE = 0.3;
export const MAGNET_R = 3.6;
export const MAGNET_CATCH = 0.75;
export const COOLANT_MUL = 0.5;
export const MINOR_HEAL = 0.4;
/** an elemental-power hit procs this often (shock arcs and freezes every shot would be silly) */
export const ELEM_PROC_CHANCE = 0.35;
export const PICKUP_R = 2.2;
/** a cowboy hovering low over a crystal (feet up to PICKUP_UP above it) or a step below it still takes it */
export const PICKUP_UP = 3.5;
export const PICKUP_DOWN = 1.5;

/** puActEl for Fire Power (elements ride as elem + 1 so 0 can mean none) */
export const PU_FIRE_EL = Elem.Fire + 1;
/** players carry the element as elem + 1 so 0 can mean none */
export const puElem = (p: PlayerState) => p.puActEl - 1;
export const puLabel = (type: number, el: number) => (type === PU.ElementalPower ? `${ELEM_NAME[el] ?? ''} POWER` : PU_DEFS[type]?.name ?? '');
export const puColor = (type: number, el: number) => (type === PU.ElementalPower ? ELEM_COLOR[el] ?? 0xffffff : PU_DEFS[type]?.color ?? 0xffffff);

/** Is this element's status kept off you by an active Elemental Power? */
export const immuneTo = (p: PlayerState, el: Elem) => p.puAct === PU.ElementalPower && puElem(p) === el;

export interface PUPickup { id: number; type: PU; el: number; x: number; y: number; z: number }

export const enum PUEv { Picked = 0, Used = 1, Expired = 2, Denied = 3, Full = 4 }

export class PowerUps {
  list: PUPickup[] = [];
  private nextId = 1;
  constructor(private g: Game) {}

  reset() { this.list = []; }

  /** Drop one on the ground (rolled from the run seed so every machine agrees on what it is). */
  drop(x: number, z: number, roll: number, roll2: number, type: PU = PU.None, yHint = 80, homeX = x, homeZ = z) {
    const g = this.g;
    if (type === PU.None) type = (1 + Math.floor(roll * (PU_COUNT - 1))) as PU;
    const el = type === PU.ElementalPower ? PU_ELEMENTS[Math.floor(roll2 * PU_ELEMENTS.length) % PU_ELEMENTS.length] : -1;
    // land on the ground the posse stands on, not the top of whatever tower or roof is overhead;
    // over the void, bring it home to the posse
    let y = g.world.support(x, z, yHint + 3);
    if (y < -1e6) { x = homeX; z = homeZ; y = g.world.support(x, z, yHint + 3); }
    if (y < -1e6) y = g.world.support(x, z, 80);
    const id = this.nextId++ & 0xff || this.nextId++;
    this.list.push({ id, type, el, x, y, z });
    return id;
  }

  /** Per tick, server side: pickups, the use button, timers and the heal-over-time. */
  step(pressedUse: Map<number, boolean>) {
    const g = this.g;
    for (const p of g.players.values()) {
      if (p.phase !== Phase.Alive || p.paused) continue;
      // walk over a drop: into the first free slot
      for (let i = 0; i < this.list.length; i++) {
        const k = this.list[i];
        if (Math.hypot(p.x - k.x, p.z - k.z) > PICKUP_R || p.y - k.y > PICKUP_UP || k.y - p.y > PICKUP_DOWN) continue;
        if (!grant(p, k.type, k.el)) continue;
        this.list.splice(i--, 1);
        g.emit(Ev.PowerUp, [p.id, k.type, k.el + 1, PUEv.Picked, k.id]);
        g.emit(Ev.Notice, [], `${(p.name || 'A COWBOY').toUpperCase()} PICKED UP ${puLabel(k.type, k.el)}.`);
      }
      if (pressedUse.get(p.id)) use(g, p);
      tickBuff(g, p);
    }
  }
}

/** Into the held slot (or the second one, once the bonus vault opened it). False when full. */
export function grant(p: PlayerState, type: number, el: number) {
  if (p.pu === PU.None) { p.pu = type; p.puEl = el + 1; return true; }
  if (p.puCap >= 2 && p.pu2 === PU.None) { p.pu2 = type; p.pu2El = el + 1; return true; }
  return false;
}

function use(g: Game, p: PlayerState) {
  const type = p.pu, el = p.puEl - 1;
  if (type === PU.None) return;
  const def = PU_DEFS[type];
  // one timed buff at a time (Godot): finish this one first
  if (def.secs > 0 && type !== PU.Stim && p.puAct !== PU.None) { g.emit(Ev.PowerUp, [p.id, type, el + 1, PUEv.Denied, 0]); return; }
  p.pu = p.pu2; p.puEl = p.pu2El; p.pu2 = PU.None; p.pu2El = 0;
  const c = CLASSES[p.cls];
  switch (type) {
    case PU.FullCharge:
      p.nades = GRENADE_MAX; p.nadeBank = 0;
      if (p.w2 !== NO_WEAPON) { const m = weaponDef(p.w2).mag; if (p.wSlot === WSlot.Special) p.mag = m; else p.mag2 = m; p.bank = 0; }
      p.shield = c.shieldMax; p.shieldDelay = 0;
      break;
    case PU.MinorHeal: p.hp = Math.min(hullMaxOf(p, modsOf(p)), p.hp + HULL_MAX * MINOR_HEAL); break;
    case PU.Stim: p.bottles = Math.min(WHISKEY_MAX, p.bottles + 1); break;
    case PU.Overshield: p.over = c.shieldMax; break;
    default:
      p.puAct = type; p.puActEl = el + 1; p.puT = secs(def.secs);
  }
  g.emit(Ev.PowerUp, [p.id, type, el + 1, PUEv.Used, 0]);
}

function tickBuff(g: Game, p: PlayerState) {
  // the whiskey's heal (stepPlayer runs its clock, so the adrenaline it carries is predicted)
  if (p.stimT > 0) p.hp = Math.min(hullMaxOf(p, modsOf(p)), p.hp + HEAL_HPS / 60);
  if (p.puAct === PU.None) return;
  if (p.puAct === PU.ElementalPower && puElem(p) === Elem.Fire) p.onFire = 0;
  if (--p.puT <= 0) {
    g.emit(Ev.PowerUp, [p.id, p.puAct, p.puActEl, PUEv.Expired, 0]);
    p.puAct = PU.None; p.puActEl = 0; p.puT = 0;
  }
}

/** Down or dead: the running buff and the overshield go (held power-ups stay in your pocket). */
export function dropBuffs(p: PlayerState) {
  p.puAct = PU.None; p.puActEl = 0; p.puT = 0; p.over = 0; p.stimT = 0;
}
